import { PresentationCountdown } from '@carboncopyco/components';
import {
  type GetModificationsArgs,
  useGetCurrentUserQuery,
  useGetDraftsQuery,
  useGetOrganizationQuery,
  useLazyGetModificationsQuery,
} from '@carboncopyco/data';
import { EditorSdkProvider } from '@carboncopyco/editor-sdk';
import type { IDemo, Modification } from '@carboncopyco/types';
import { type IGlobalTab, TabType } from '@carboncopyco/types/desktop';
import { ColorHex, LoadingDots } from '@carboncopyco/ui';
import { useAsyncEffect } from 'ahooks';
import classNames from 'classnames';
import { type CSSProperties, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { settings } from '../../../../settings';
import { isArchivedDemoOrPresentation } from '../../../shared/globalTabUtil';
import InnerTabsContainer from '../../components/InnerTabs/InnerTabsContainer';
import InnerViewArea from '../../components/InnerViewArea';
import { useDelayLoadingPlaybookTab } from '../../hooks/useDelayLoadingPlaybookTab';
import { useFlagsFallback } from '../../hooks/useFlagsFallback';
import { useFocusAppService } from '../../hooks/useFocusAppService';
import useSDKCommunicationLayer from '../../hooks/useSDKCommunicationLayer';
import rendererService from '../../services/RendererService';
import { useTabManager } from '../../services/TabManager';
import { isActiveOrAllInnerTabsLoaded } from '../../shared/utils';
import debuggerInterceptorPathListState from '../../shared/utils/debugger-interceptor-path-list-state';
import { useUser } from '../../store/context';
import { ResponsiveModeContextProvider } from '../../store/context/ResponsiveModeContext';
import { previewFrameBorder } from '../constants/previewFrameBorder';
import DemoPlayScreenToolbar from './DemoPlayScreenToolbar';
import useIsDemoPlayReady from './useIsDemoPlayReady';

const PRESENTATION_TIMER_INTERVAL_IN_MS = 800;

type ModificationArg =
  | 'edit'
  | 'replace_image'
  | 'replace_text'
  | 'replace_image_with_variable'
  | 'snippet'
  | 'replace_link'
  | 'block_interactions'
  | 'variable'
  | 'image_variable'
  | 'transaction_rule'
  | 'guidance_tooltip';

const isPreviewingExistingPlaybook = (tab: IGlobalTab) =>
  tab.type === TabType.GROUP_DEMO_PREVIEW && tab.payload.play?.organizationId;

const getEntityType = (tab: IGlobalTab) => {
  if (
    tab.type === TabType.PRESENT_DEMO ||
    tab.type === TabType.GROUP_DEMO_PRESENTATION ||
    isPreviewingExistingPlaybook(tab)
  ) {
    return 'presentation';
  }

  return [
    TabType.PREVIEW_DEMO,
    TabType.PREVIEW_OVERLAY,
    TabType.GROUP_DEMO_PREVIEW,
  ].includes(tab.type) && tab.payload.showWithoutDrafts
    ? 'demo'
    : 'draft';
};

const DemoPlayScreenContent = ({ tab }: { tab: IGlobalTab }) => {
  const [pageLoaded, setPageLoaded] = useState(false);
  const [startCounter, setStartCounter] = useState(false);
  const [counterDone, setCounterDone] = useState(false);

  const { activeOrganization } = useUser();
  const { getActiveGlobalTab, updateGlobalTabPayload } = useTabManager();

  const { triggerFocus } = useFocusAppService();
  const { countdownTimer } = useFlagsFallback();
  const communicationLayer = useSDKCommunicationLayer(tab.id);

  const activeTab = getActiveGlobalTab();
  const isInnerViewLoaded = isActiveOrAllInnerTabsLoaded(tab);
  const { updateImageReplacePathListStateModification } =
    debuggerInterceptorPathListState(tab);
  const entityType = getEntityType(tab);

  const { showWithoutDrafts } = tab.payload;
  const { organizationId: demoOrganizationId, id: demoDemoId } =
    (tab.payload.demo as IDemo) || {};

  const params = useParams();

  const organizationId = params?.organizationId ?? demoOrganizationId;
  const demoId = params?.demoId ?? demoDemoId.toString();

  const isActiveTab = activeTab?.id === tab.id;
  const isPresentScreen =
    tab.type === TabType.PRESENT_DEMO ||
    tab.type === TabType.GROUP_DEMO_PRESENTATION;
  const isPreviewScreen =
    tab.type === TabType.PREVIEW_DEMO ||
    tab.type === TabType.PREVIEW_OVERLAY ||
    tab.type === TabType.GROUP_DEMO_PREVIEW;

  const { isReady } = useIsDemoPlayReady({
    tab,
    organizationId,
    demoId,
  });

  const { data: organization } = useGetOrganizationQuery(
    { id: activeOrganization?.id?.toString() },
    { skip: !activeOrganization?.id }
  );

  const { data: userData } = useGetCurrentUserQuery();

  const { data: drafts } = useGetDraftsQuery(
    {
      organizationId: activeOrganization?.id?.toString(),
      sourceType: 'demo',
      sourceId: +(tab.cloneId ?? ''),
    },
    {
      skip: showWithoutDrafts || !activeOrganization || !tab.cloneId,
      refetchOnMountOrArgChange: true,
    }
  );

  // We don't use here const [{ data: modifications }] = useLazyGetModificationsQuery(),
  // because it changes two times but the data is the same. Might be the bug
  // in query implementation. RND-13026
  const [modifications, setModifications] =
    useState<Modification<ModificationArg>[]>();
  const [getModifications] = useLazyGetModificationsQuery();

  const user = userData?.user;
  const draft = drafts?.[0];
  const entityId =
    tab.payload[entityType]?.id ??
    tab.payload[entityType]?.presentationId ??
    draft?.id;

  useEffect(() => {
    if (isInnerViewLoaded && !pageLoaded && isActiveTab) {
      setPageLoaded(true);
      if (!counterDone) setStartCounter(true);
    }
  }, [isInnerViewLoaded]);

  const [canStartLoadingTab, setCanStartLoadingTab] = useState(isActiveTab);
  const {
    delayLoadingPlaybookTab,
    logPlaybookTabLoad,
    setTabLoadingOverriden,
    shouldLoadInBackground,
  } = useDelayLoadingPlaybookTab(tab);

  useEffect(() => {
    delayLoadingPlaybookTab(() => setCanStartLoadingTab(true));
  }, []);

  useEffect(() => {
    if (isActiveTab) {
      setCanStartLoadingTab(true);
      setTabLoadingOverriden();
    }
  }, [isActiveTab]);

  useEffect(() => {
    if (!entityId || !isReady || !canStartLoadingTab) {
      return;
    }
    logPlaybookTabLoad();
    const modificationsPayload: GetModificationsArgs = {
      organizationId: activeOrganization.id.toString(),
      entityType,
      entityId: String(entityId ?? ''),
      apiVersion: 2,
    };
    if (
      [TabType.GROUP_DEMO_PREVIEW, TabType.PREVIEW_DEMO].includes(tab.type) &&
      !tab.payload.play?.organizationId &&
      tab.payload?.presentation?.revisionId !== undefined
    ) {
      modificationsPayload.revisionId =
        tab.payload.presentation.revisionId.toString();
    }

    getModifications(modificationsPayload)
      .unwrap()
      .then((result) => {
        setModifications(result);
        updateImageReplacePathListStateModification(result);
        return null;
      })
      .catch((error) => {
        console.error('Error fetching modifications', error);
      });
  }, [entityId, isReady, canStartLoadingTab]);

  useAsyncEffect(async () => {
    if (!modifications) {
      return;
    }

    const updatedPayload = {
      ...tab.payload,
      modifications,
      shouldLoadInBackground,
    };

    updateGlobalTabPayload(tab.id, updatedPayload);

    rendererService.createGlobalTab({
      ...tab,
      payload: updatedPayload,
    });
  }, [modifications]);

  if (!organization || !user) {
    return null;
  }

  const isContentLoaded = isInnerViewLoaded && isReady;

  return (
    <EditorSdkProvider
      editorSdkId={String(tab.id)}
      communicationLayer={communicationLayer}
      mode="preview"
      organizationData={organization}
      userData={user}
      draft={draft}
      modifications={modifications}
      filesServiceDomain={settings.FILES_SERVICE_URL ?? ''}
    >
      <ResponsiveModeContextProvider>
        <div className="flex flex-col flex-grow flex-shrink-0 basis-auto">
          <DemoPlayScreenToolbar
            isPresentMode={isPresentScreen}
            isSpeakerPannelVisible={
              (!tab?.payload?.isOpenedFromEditor ||
                isPresentScreen ||
                tab.type === TabType.GROUP_DEMO_PREVIEW) &&
              !isArchivedDemoOrPresentation(tab)
            }
            tab={tab}
          />
          {isContentLoaded && <InnerTabsContainer globalTab={tab} />}
          <div
            className={classNames('flex grow relative', {
              [previewFrameBorder]: isPreviewScreen,
            })}
          >
            <InnerViewArea tab={tab} />
            {!isContentLoaded && (
              <div className="absolute inset-0 w-full h-full flex justify-center items-center bg-ds-accent3-100">
                <LoadingDots color={ColorHex.natural600} />
              </div>
            )}
          </div>
          {countdownTimer && startCounter && isPresentScreen && (
            <PresentationCountdown
              onCounterStart={() => triggerFocus(true)}
              counterInterval={PRESENTATION_TIMER_INTERVAL_IN_MS}
              onCounterDone={() => {
                triggerFocus(false);
                setStartCounter(false);
                setCounterDone(true);
              }}
              counterTextStyle={
                {
                  '--tw-text-opacity': '0.6',
                } as CSSProperties
              }
            />
          )}
        </div>
      </ResponsiveModeContextProvider>
    </EditorSdkProvider>
  );
};

export default DemoPlayScreenContent;
